import React, { useEffect, useCallback, useRef, useState } from "react";
import PropTypes from "kf-proptypes/widgets";
import { v4 } from "uuid";

import { Menu, MenuItem, MENU_SIZES } from "./menu.jsx";

import "antd/es/select/style/css";
import "antd/es/menu/style/css";

import "./menu.css";

const defaultRenderer = (item, index) => {
  return <MenuItem key={index}>{item}</MenuItem>;
};

const defaultValueComparer = (value, data) => {
  return data.Name === value;
};

/**
 * Dropdown Menu is a component with list of menu item to represent as dropdown
 */
export function DropMenu(props) {
  const {
    isVisible,
    dataSource = [],
    onChange = () => {},
    onSelect,
    value,
    valueComparer = defaultValueComparer,
    itemRenderer = defaultRenderer,
    size
  } = props;
  let [currentIndex, setCurrentIndex] = useState(0);
  const [menuItems, setMenuItems] = useState([]);
  const containerId = useRef(`singleselect_${v4()}`);
  const scrollRef = useRef(null);

  const handleKeyDown = useCallback(
    function keyDownMemoized(e) {
      if (e.key === "ArrowDown") {
        ++currentIndex;
        if (currentIndex === dataSource.length) {
          // eslint-disable-next-line react-hooks/exhaustive-deps
          currentIndex = 0;
        }
        setCurrentIndex(currentIndex);
        onChange(dataSource[currentIndex]);
        fixScrollbarFocus();
      } else if (e.key === "ArrowUp") {
        --currentIndex;
        if (currentIndex === -1) {
          currentIndex = dataSource.length - 1;
        }
        setCurrentIndex(currentIndex);
        onChange(dataSource[currentIndex]);
        fixScrollbarFocus();
      } else if (e.key === "Enter") {
        onSelect(dataSource[currentIndex]);
      }
    },
    [currentIndex, dataSource]
  );

  useEffect(
    function visibleEffect() {
      if (isVisible) {
        window.addEventListener("keydown", handleKeyDown);
      } else {
        window.removeEventListener("keydown", handleKeyDown);
      }
      return () => window.removeEventListener("keydown", handleKeyDown);
    },
    [handleKeyDown, isVisible]
  );

  useEffect(
    function menuEffect() {
      setMenuItems(dataSource.map((item, index) => itemRenderer(item, index)));
    },
    [dataSource, itemRenderer]
  );

  useEffect(
    function dataSourceEffect() {
      if (value) {
        const selectedIndex = dataSource.findIndex((data) =>
          valueComparer(value, data)
        );
        setCurrentIndex(selectedIndex);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataSource, value]
  );

  const fixScrollbarFocus = useCallback(function fixScrollbarFocusMemoized() {
    setTimeout(() => {
      let activeOptionsEle = document.querySelector(
        `#${containerId.current} .ant-menu-item-active`
      );
      if (activeOptionsEle) {
        const activeEleStart = activeOptionsEle.getBoundingClientRect().top;
        const activeEleEnd =
          activeEleStart + activeOptionsEle.getBoundingClientRect().height;

        const scrollElStart = scrollRef.current.getBoundingClientRect().top;
        const scrollElEnd =
          scrollElStart + scrollRef.current.getBoundingClientRect().height;

        if (activeEleStart < scrollElStart) {
          activeOptionsEle.scrollIntoView({ behavior: "smooth" });
        } else if (activeEleEnd > scrollElEnd) {
          scrollRef.current.scroll({ y: `${activeEleEnd - scrollElEnd}px` });
        }
      }
    }, 100);
  }, []);

  useEffect(
    function currentEffect() {
      setTimeout(() => {
        document
          .querySelectorAll(`#${containerId.current} .ant-menu-item`)
          .forEach((menuItem, index) => {
            if (index === currentIndex) {
              menuItem.classList.add("ant-menu-item-active");
            } else {
              menuItem.classList.remove("ant-menu-item-active");
            }
          });
      }, 50);
    },
    [currentIndex]
  );

  return (
    <div style={{ maxHeight: "256px", overflowY: "auto" }} ref={scrollRef}>
      <div style={{ maxHeight: "256px" }}>
        <Menu
          id={containerId.current}
          onChange={(e) => onSelect(dataSource[e.key])}
          size={size}
        >
          {menuItems}
        </Menu>
      </div>
    </div>
  );
}

DropMenu.propTypes = {
  /**
   * To enable Keyboard navigation
   */
  isVisible: PropTypes.bool,
  /**
   * Value to display in drop menu
   */
  dataSource: PropTypes.oneOf([
    PropTypes.arrayOf(PropTypes.array),
    PropTypes.func
  ]),
  /**
   * User can customise the list of dropmenu using this prop
   */
  itemRenderer: PropTypes.func,
  /**
   * Trigger when user onclick the item in list
   */
  onChange: PropTypes.func,
  /**
   * Trigger when user selects the item in list
   */
  onSelect: PropTypes.func,
  /**
   * Already selected value will be given via this prop
   */
  value: PropTypes.string,
  /**
   * To find selected value from dataSource item
   */
  valueComparer: PropTypes.func,
  /**
   * Size of the dropdown menu
   */
  size: PropTypes.string
};

DropMenu.defaultProps = {
  dataSource: [],
  onChange: () => {},
  itemRenderer: defaultRenderer
};

// eslint-disable-next-line kf/const-naming-convention
export const DropMenuItem = MenuItem;
export const DROP_MENU_SIZES = MENU_SIZES;
